import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Download, FileText, ExternalLink, Home, Maximize2, Bed, Bath } from 'lucide-react';
import { getImageUrl } from '../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MyProjects = () => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/customer/my-projects`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setProjects(response.data.projects || []);
    } catch (error) {
      console.error('Erro ao buscar projetos:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const getDocumentIcon = (type) => {
    switch (type) {
      case 'arquitetonico':
        return <Home size={18} className="text-[#00A86B]" />;
      case 'interiores':
        return <Bed size={18} className="text-[#00A86B]" />;
      default:
        return <FileText size={18} className="text-[#00A86B]" />;
    }
  };

  const handleDownload = (url, fileName) => {
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <Card className="p-8">
        <p className="text-center text-gray-500">Carregando projetos...</p>
      </Card>
    );
  }

  if (projects.length === 0) {
    return (
      <Card className="p-8">
        <div className="text-center">
          <FileText size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500 mb-2">Você ainda não possui projetos adquiridos.</p>
          <p className="text-sm text-gray-400">
            Os projetos aparecerão aqui após a confirmação do pagamento.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Meus Projetos</h2>
      
      {/* Informativo de Entrega */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-800">
              <strong className="font-semibold">📧 Informação sobre entrega:</strong> Seu projeto será enviado para o seu e-mail em até <strong>72 horas</strong> após a confirmação do pagamento. Os documentos também poderão ser disponibilizados nesta área para que você realize o download diretamente.
            </p>
          </div>
        </div>
      </div>
      
      <p className="text-sm text-gray-600 mb-6">
        Aqui estão todos os projetos que você adquiriu. Você pode baixar os documentos técnicos a qualquer momento.
      </p>

      <div className="grid grid-cols-1 gap-6">
        {projects.map((project) => (
          <Card key={project.id} className="overflow-hidden">
            <div className="md:flex">
              {/* Imagem do Projeto */}
              <div className="md:w-1/3">
                {project.images && project.images.length > 0 ? (
                  <img
                    src={getImageUrl(project.images[0])}
                    alt={project.name}
                    className="w-full h-full object-cover"
                    style={{ minHeight: '250px', maxHeight: '300px' }}
                  />
                ) : (
                  <div className="w-full h-64 bg-gray-200 flex items-center justify-center">
                    <Home size={48} className="text-gray-400" />
                  </div>
                )}
              </div>

              {/* Informações do Projeto */}
              <div className="md:w-2/3 p-6">
                <div className="mb-4">
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">{project.name}</h3>
                  <p className="text-sm text-gray-500 mb-3">
                    Adquirido em {formatDate(project.purchased_at)}
                  </p>

                  {/* Características do Projeto */}
                  <div className="flex flex-wrap gap-4 mb-4">
                    {project.area && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Maximize2 size={16} className="text-[#00A86B]" />
                        <span>{project.area}</span>
                      </div>
                    )}
                    {project.bedrooms > 0 && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Bed size={16} className="text-[#00A86B]" />
                        <span>{project.bedrooms} quarto{project.bedrooms > 1 ? 's' : ''}</span>
                      </div>
                    )}
                    {project.bathrooms > 0 && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Bath size={16} className="text-[#00A86B]" />
                        <span>{project.bathrooms} banheiro{project.bathrooms > 1 ? 's' : ''}</span>
                      </div>
                    )}
                  </div>

                  {project.description && (
                    <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                      {project.description}
                    </p>
                  )}
                </div>

                {/* Documentos Técnicos */}
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <FileText size={18} className="mr-2 text-[#00A86B]" />
                    Documentos Técnicos Disponíveis
                  </h4>

                  {project.documents && project.documents.length > 0 ? (
                    <div className="space-y-2">
                      {project.documents.map((doc, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-3 flex-1">
                            {getDocumentIcon(doc.type)}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-800 truncate">
                                {doc.name}
                              </p>
                              <p className="text-xs text-gray-500">{doc.type_label}</p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleDownload(doc.url, doc.name)}
                            className="bg-[#00A86B] hover:bg-[#00A86B]/90 text-white"
                          >
                            <ExternalLink size={16} className="mr-2" />
                            Acessar
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-sm text-yellow-800">
                        ⚠️ Nenhum documento técnico disponível ainda. Entre em contato com o suporte.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default MyProjects;
